'use client';

import { Icon } from '@iconify/react';
import gsap from 'gsap';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const testimonials = [
    {
        nameKey: 'Testimonials.items.0.name',
        roleKey: 'Testimonials.items.0.role',
        quoteKey: 'Testimonials.items.0.quote',
        icon: 'carbon:user-avatar-filled',
    },
    {
        nameKey: 'Testimonials.items.1.name',
        roleKey: 'Testimonials.items.1.role',
        quoteKey: 'Testimonials.items.1.quote',
        icon: 'carbon:user-avatar-filled',
    },
    {
        nameKey: 'Testimonials.items.2.name',
        roleKey: 'Testimonials.items.2.role',
        quoteKey: 'Testimonials.items.2.quote',
        icon: 'carbon:user-avatar-filled',
    },
];

export function TestimonialsSection() {
    const { t } = useTranslation();
    const sectionRef = useRef<HTMLElement>(null);
    const cardsRef = useRef<HTMLDivElement>(null);
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        if (!sectionRef.current || !cardsRef.current) return;

        gsap.set(sectionRef.current, { opacity: 0 });
        const tl = gsap.timeline();

        tl.to(sectionRef.current, { opacity: 1, duration: 0.5 });
        tl.fromTo(
            cardsRef.current.children,
            { y: 30, opacity: 0 },
            { y: 0, opacity: 1, duration: 0.5, stagger: 0.15, ease: 'power2.out' },
            '-=0.3'
        );
    }, []);

    // Auto-rotate testimonials
    useEffect(() => {
        const interval = setInterval(() => {
            setActiveIndex((prev) => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    return (
        <section ref={sectionRef} className="mb-12 sm:mb-16">
            <div className="flex items-center gap-3 mb-8">
                <div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                    <Icon icon="carbon:star-filled" className="h-5 w-5 text-amber-500" />
                </div>
                <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 dark:text-amber-500 mb-1">
                        {t('Testimonials.sectionLabel')}
                    </p>
                    <h2 className="text-2xl sm:text-4xl font-bold text-slate-900 dark:text-white tracking-tight">
                        {t('Testimonials.sectionTitle')}
                    </h2>
                    <p className="text-base text-slate-700 dark:text-muted-foreground mt-2 max-w-2xl">
                        {t('Testimonials.sectionSubtitle')}
                    </p>
                </div>
            </div>

            <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {testimonials.map((testimonial, index) => (
                    <div
                        key={testimonial.nameKey}
                        className={`relative bg-white dark:bg-card border rounded-2xl p-6 sm:p-8 transition-all duration-500 flex flex-col h-full ${index === activeIndex
                            ? 'border-primary/40 dark:border-primary/50 shadow-xl shadow-primary/5 dark:shadow-primary/10 scale-[1.02] z-10'
                            : 'border-slate-200 dark:border-slate-700/60'
                            }`}
                    >
                        {/* Quote icon */}
                        <div className="flex justify-between items-start mb-4">
                            <Icon
                                icon="carbon:quotes"
                                className="h-8 w-8 text-primary/20"
                            />
                        </div>

                        <p className="text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed mb-6 italic">
                            &ldquo;{t(testimonial.quoteKey)}&rdquo;
                        </p>

                        <div className="mt-auto">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-600 flex items-center justify-center">
                                    <Icon icon={testimonial.icon} className="h-6 w-6 text-slate-500 dark:text-slate-400" />
                                </div>
                                <div>
                                    <p className="text-sm font-bold text-slate-900 dark:text-white">
                                        {t(testimonial.nameKey)}
                                    </p>
                                    <p className="text-xs text-slate-500 dark:text-muted-foreground">
                                        {t(testimonial.roleKey)}
                                    </p>
                                </div>
                            </div>

                            <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
                                <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
                                    {t(testimonial.nameKey.replace('name', 'result'))}
                                </span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Dots indicator */}
            <div className="flex justify-center gap-2 mt-4">
                {testimonials.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => setActiveIndex(index)}
                        className="group relative h-11 w-8 flex items-center justify-center -my-2"
                        aria-label={`View testimonial ${index + 1}`}
                    >
                        <span className={`h-2 rounded-full transition-all duration-300 ${index === activeIndex
                            ? 'w-6 bg-primary'
                            : 'w-2 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700/60'
                            }`} />
                    </button>
                ))}
            </div>
        </section>
    );
}
